import type { Sink } from "./sinks";

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

/** Registers /sw.js once. Safe to call repeatedly; resolves null when unsupported. */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return Promise.resolve(null);
  if (!registration) {
    registration = navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((r) => r)
      .catch(() => null);
  }
  return registration;
}

/** True when a controlling service worker can be obtained (after activation + clients.claim). */
export async function serviceWorkerSinkAvailable(): Promise<boolean> {
  if (!("serviceWorker" in navigator) || !window.isSecureContext) return false;
  const reg = await registerServiceWorker();
  if (!reg) return false;
  await navigator.serviceWorker.ready;
  for (let i = 0; i < 30 && !navigator.serviceWorker.controller; i++) await sleep(100);
  return Boolean(navigator.serviceWorker.controller);
}

/**
 * Streams decrypted bytes to disk via the service worker (see public/sw.js).
 * One chunk is sent per `pull` request from the worker — bounded memory, natural backpressure.
 */
export async function createServiceWorkerSink(fileName: string, size: number): Promise<Sink> {
  const controller = navigator.serviceWorker.controller;
  if (!controller) throw new Error("Service worker is not controlling this page");

  const channel = new MessageChannel();
  const id = crypto.randomUUID();
  const ready = new Promise<string>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Service worker did not respond")), 5000);
    channel.port1.onmessage = (e) => {
      if (e.data?.ready) {
        clearTimeout(timer);
        resolve(String(e.data.url));
      }
    };
  });
  controller.postMessage({ type: "lsc-stream", id, name: fileName, size }, [channel.port2]);
  const url = await ready;

  // Navigating a hidden iframe to the SW URL makes the browser start the download.
  const iframe = document.createElement("iframe");
  iframe.hidden = true;
  iframe.src = url;
  document.body.appendChild(iframe);
  const removeFrame = () => setTimeout(() => iframe.remove(), 10_000);

  const port = channel.port1;
  let pullResolvers: (() => void)[] = [];
  let failed: Error | null = null;
  port.onmessage = (e) => {
    const m = e.data;
    if (m?.pull) {
      const r = pullResolvers.shift();
      if (r) r();
      else pulls++;
    } else if (m?.cancelled) {
      failed = new Error("Download cancelled by the browser");
      pullResolvers.splice(0).forEach((r) => r());
    } else if (m?.closed) {
      removeFrame();
    }
  };
  let pulls = 0;
  const waitForPull = () =>
    new Promise<void>((resolve) => {
      if (failed) return resolve();
      if (pulls > 0) {
        pulls--;
        resolve();
      } else {
        pullResolvers.push(resolve);
      }
    });

  return {
    kind: "serviceworker",
    write: async (stream) => {
      const reader = stream.getReader();
      try {
        for (;;) {
          await waitForPull();
          if (failed) throw failed;
          const { value, done } = await reader.read();
          if (done) break;
          // Transfer the buffer: zero-copy hand-off, and the page cannot accidentally retain it.
          const copy = value.byteOffset === 0 && value.byteLength === value.buffer.byteLength ? value : value.slice();
          port.postMessage({ chunk: copy.buffer }, [copy.buffer]);
        }
        port.postMessage({ done: true });
      } catch (e) {
        port.postMessage({ abort: true });
        await reader.cancel().catch(() => undefined);
        removeFrame();
        throw e;
      }
    },
    abort: async () => {
      port.postMessage({ abort: true });
      removeFrame();
    },
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
